import { getDisplayedAvatar, HOUSEHOLD_MEMBER_AVATARS, HOUSEHOLD_MEMBER_COLORS } from '../caremap/CareMapFactory';
import { IAvatarIdentity } from './IAvatarIdentity';

// these need to be in hex so we can compute relative luminance and contrast ratios, and they need to be in sync with the CSS variables
const LIGHT = '#FFFFFF'; // --ion-background-color
const DARK = '#425f69'; // --ion-color-step-750

// One tone behind every state that is not a rendered image: we do not know yet, we are loading,
// and the image failed. Sharing it means moving between those states never flashes a background
// change, and a screenshot of a stuck avatar is distinguishable from a transparent circle.
const NEUTRAL_FILL = 'var(--ion-color-step-100)';

/**
 * What an avatar draws for one person, before any loading is considered.
 *
 * `imageVisual` and `fallbackVisual` are what the avatar publishes as data-avatar while it shows the
 * image or the fallback. A photo goes out as 'photo' and initials as 'initials' rather than the url
 * or the letters, so this attribute, which session replay records, carries neither.
 */
export interface IAvatarDescription {
    // the illustration to try when there is no photo
    illustration?: string;
    hasImage: boolean;
    initials: string;
    // initials, or the person figure when there are none, on the person's colour
    showsIdentity: boolean;
    // the circle's fill whenever no image has loaded
    background: string;
    // the initials' or figure's colour on that fill
    foreground: string;
    imageVisual?: string;
    fallbackVisual?: string;
}

// The illustrated avatars carry their palette colour at roughly 30% over white rather than at
// full strength -- measured off the artwork, where e.g. 3dog.png's background wash is at alpha 77
// of 255. Initials and the placeholder figure use the same treatment so a circle of letters sits
// at the same visual weight as a circle holding an illustration.
const SURFACE_OPACITY = 0.3;

const firstLetter = (word: string): string => word.match(/\p{L}/u)?.[0] || '';

/**
 * FNV-1a. Any stable arithmetic hash would do; what matters is that it is deterministic across
 * sessions and platforms, so a person's colour never changes underneath them.
 */
const hash = (value: string): number => {
    let result = 0x811c9dc5;
    for (let i = 0; i < value.length; i++) {
        result ^= value.charCodeAt(i);
        result = Math.imul(result, 0x01000193);
    }
    return Math.abs(result);
};

const toLinear = (channel: number): number => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};

const relativeLuminance = (hex: string): number => {
    if (!hex.startsWith('#')) {
        return NaN;
    }
    const value = hex.replace('#', '');
    const r = parseInt(value.substring(0, 2), 16);
    const g = parseInt(value.substring(2, 4), 16);
    const b = parseInt(value.substring(4, 6), 16);
    return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

/**
 * What an avatar draws when it has no photo: which illustration, a person's initials, their identity
 * colour, and text that stays legible on it.
 */
export class AvatarPresentation {
    /**
     * A person always gets a colour, whether or not we can spell them: initials when we have a name,
     * the placeholder figure when we do not. That colour is what separates "this is someone we know"
     * from a failed image, which keeps the neutral fill. A care map member with no photo or
     * illustration is their chosen colour. Undefined identity means not loaded yet: a plain neutral
     * circle, so no fallback appears before the real picture.
     *
     * `identifiesByPhotoOrInitials` draws only what identifies the person, their photo or else their
     * initials on their colour, never an illustration or a bare colour. Their colour is the one they
     * chose, else the one a chosen illustration is drawn on, else their name's.
     */
    describeAvatar = (params: {
        identity: IAvatarIdentity | undefined;
        name: string;
        fallsBackToIllustration: boolean;
        identifiesByPhotoOrInitials?: boolean;
    }): IAvatarDescription => {
        const { identity, name, fallsBackToIllustration, identifiesByPhotoOrInitials = false } = params;

        const illustration = identity && !identifiesByPhotoOrInitials ? this.getIllustration({
            photo: identity.photo,
            avatar: identity.avatar,
            color: identity.color,
            fallsBackToIllustration
        }) : undefined;
        const hasImage = !!identity && (!!identity.photo || !!illustration);
        const initials = this.getInitials(name);
        const showsIdentity = !hasImage && !!identity && (identifiesByPhotoOrInitials || !fallsBackToIllustration);
        const colorOnly = !hasImage && fallsBackToIllustration && !identifiesByPhotoOrInitials ? identity?.color : undefined;

        const identityBackground = this.toAvatarSurfaceColor(this.getIdentityColor({ identity, name, identifiesByPhotoOrInitials }));
        const background = (showsIdentity ? identityBackground : colorOnly) || NEUTRAL_FILL;

        let fallbackVisual: string | undefined = identity?.color;
        if (showsIdentity) {
            fallbackVisual = initials ? 'initials' : 'placeholder';
        }

        return {
            illustration,
            hasImage,
            initials,
            showsIdentity,
            background,
            foreground: this.getContrastingTextColor(identityBackground),
            imageVisual: this.getImageVisual({ photo: identity?.photo, illustration }),
            fallbackVisual
        };
    };

    // The palette colour an illustration's wash is drawn in; each illustration has the colour at its index.
    getIllustrationColor = (avatar: string | undefined): string | undefined => {
        const index = avatar ? HOUSEHOLD_MEMBER_AVATARS.indexOf(avatar) : -1;
        return index >= 0 ? HOUSEHOLD_MEMBER_COLORS[index] : undefined;
    };

    // The colour behind someone's initials, before the surface tint: the one they chose, else in the
    // agency look a chosen illustration's, else their name's.
    getIdentityColor = ({ identity, name, identifiesByPhotoOrInitials }: {
        identity: IAvatarIdentity | undefined;
        name: string;
        identifiesByPhotoOrInitials: boolean;
    }): string => {
        const illustrationColor = identifiesByPhotoOrInitials ? this.getChosenIllustrationColor(identity?.avatar) : undefined;
        return identity?.color || illustrationColor || this.getAvatarColor(name);
    };

    // The colour of an illustration someone chose. The bee is what onboarding and an agency's add-client
    // store for everyone who chose nothing, so it says nothing about them and would make nearly all blue.
    private getChosenIllustrationColor = (avatar: string | undefined): string | undefined => {
        return avatar === HOUSEHOLD_MEMBER_AVATARS[0] ? undefined : this.getIllustrationColor(avatar);
    };

    // What an avatar publishes as data-avatar while it shows its image.
    private getImageVisual = ({ photo, illustration }: { photo?: string; illustration?: string; }): string | undefined => {
        return photo ? 'photo' : illustration;
    };

    /**
     * The illustration to draw for someone with no photo. A care map member falls back to the bee
     * when they chose nothing; a person falls back to their initials, so only a chosen illustration
     * counts.
     */
    getIllustration = (params: { photo?: string; avatar?: string; color?: string; fallsBackToIllustration: boolean }): string | undefined => {
        const { photo, avatar, color, fallsBackToIllustration } = params;
        return fallsBackToIllustration ? getDisplayedAvatar({ photo, avatar, color }) : avatar;
    };

    /**
     * Two letters, from the first and last word of the name. A single-word name yields one letter,
     * and an empty name yields an empty string -- callers treat that as "no identity to show". Only
     * letters count, so punctuation and numbers never become initials.
     */
    getInitials = (name: string | undefined): string => {
        const letters = (name || '').trim().split(/\s+/).map(firstLetter).filter(Boolean);
        if (letters.length === 0) {
            return '';
        }
        const last = letters.length > 1 ? letters[letters.length - 1] : '';
        return `${letters[0]}${last}`.toUpperCase();
    };

    /**
     * Derived from the whole name rather than the initials, so people who share initials are spread
     * across the palette instead of all sharing one colour. Deriving from the name rather than a user
     * id keeps it available everywhere initials are rendered without threading extra props, and means
     * the colour stays coherent with the letters if someone's name changes.
     */
    getAvatarColor = (name: string | undefined): string => {
        const normalized = (name || '').trim().toLowerCase().replace(/\s+/g, ' ');
        return HOUSEHOLD_MEMBER_COLORS[hash(normalized) % HOUSEHOLD_MEMBER_COLORS.length];
    };

    toAvatarSurfaceColor = (hex: string): string => {
        const value = hex.replace('#', '');
        if (value.length !== 6) {
            return hex;
        }
        const blend = (channel: number): number => Math.round(channel * SURFACE_OPACITY + 255 * (1 - SURFACE_OPACITY));
        const r = blend(parseInt(value.substring(0, 2), 16));
        const g = blend(parseInt(value.substring(2, 4), 16));
        const b = blend(parseInt(value.substring(4, 6), 16));
        return `#${[r, g, b].map(c => c.toString(16).padStart(2, '0').toUpperCase()).join('')}`;
    };

    /**
     * Computed rather than held in a per-colour map, because parts of the palette are too light for
     * white text and a hand-maintained map silently rots when a colour is added or changed.
     */
    getContrastingTextColor = (backgroundHex: string): string => {
        const luminance = relativeLuminance(backgroundHex);
        if (Number.isNaN(luminance)) {
            // a colour we cannot parse tells us nothing about contrast, and dark text is legible on
            // more of the palette than white is
            return DARK;
        }
        const contrastWithWhite = (relativeLuminance(LIGHT) + 0.05) / (luminance + 0.05);
        const contrastWithDark = (luminance + 0.05) / (relativeLuminance(DARK) + 0.05);
        return contrastWithWhite >= contrastWithDark ? LIGHT : DARK;
    };
}
